// University of Maryland, Baltimore County: public college page (#2652,
// round 2), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UMBC's Common Data Set 2025-2026 (Institutional Research, Analysis &
//    Decision Support): C1 applicants, admits and enrollees for Fall 2025
//    with the residency table, C8 test policy for Fall 2027, C21/C22 plans.
//    Quirk: IRADS hosts the PDF on UMBC's Box account; the source link is the
//    IRADS Common Data Set page that links it. The CDS suppresses small cells
//    as "C or t" (the unknown-residency column), so the residency rows add to
//    the totals for applicants and admits but 3 enrollees are unassigned.
//  - UMBC Undergraduate Admissions: Dates & Deadlines (fall enrollment
//    deadlines, decision letter timing, arts audition and portfolio dates)
//    and How to Apply to UMBC as a First-Year Student ("Apply for Spring
//    2027 and Fall 2027": fee, test scores, Honors College supplement).
//  - UMBC Financial Aid, 2026-2027 Undergraduate Cost of Attendance, living
//    on campus. The CDS G1/G5 figures are 2025-2026 (it says 2026-2027 costs
//    would come in July 2026), so the financial aid page is used.
//  - College Scorecard API, school id 163268 (net price by family income).
// Cost departure: the financial aid page's in-state total ($42,456) is $180
// above the sum of its own items ($42,276); its in-state housing line reads
// $9,248 while the out-of-state line reads $9,428. The rows here are the
// page's items as printed and the total is their sum. Tuition and fees are
// marked on that page as estimated (proposed rates).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UMBC Common Data Set 2025-2026',
        url: 'https://irads.umbc.edu/common-data-set-cds/',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'UMBC Undergraduate Admissions, Dates & Deadlines',
        url: 'https://umbc.edu/admissions/undergraduate/dates-deadlines/',
        read: '2026-10-01',
    },
    howToApply: {
        label: 'UMBC Undergraduate Admissions, How to Apply as a First-Year Student',
        url: 'https://umbc.edu/admissions/undergraduate/first-year-students/how-to-apply/',
        read: '2026-10-01',
    },
    cost: {
        label: 'UMBC Financial Aid, 2026-2027 Undergraduate Cost of Attendance',
        url: 'https://financialaid.umbc.edu/8119-2/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?163268-University-of-Maryland-Baltimore-County',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-maryland-baltimore-county',
    kbId: 'university_of_maryland_baltimore_county',
    name: 'University of Maryland, Baltimore County',
    shortName: 'UMBC',
    aliases: ['UMBC'],
    location: 'Baltimore, MD',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UMBC style guide); decoration only.
    branding: {
        primary: '#000000',
        secondary: '#FDB515',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_maryland_baltimore_county_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 14802,
        admitted: 10949,
        enrolled: 2271,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 11675, admitted: 8879 },
            { group: 'Out-of-state', applied: 1886, admitted: 1148 },
            { group: 'International', applied: 1241, admitted: 922 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UMBC\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UMBC has no early decision plan. The Common Data Set lists a nonbinding, non-restrictive early action plan (closing November 1, notification December 15) but gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UMBC as a whole. Some programs ask for more:',
        rows: [
            { school: 'Visual and performing arts majors', requirement: 'Acting and dance applicants audition, music applicants apply and audition, and visual arts applicants send a portfolio, each on its own dates and deadlines.' },
            { school: 'Honors College', requirement: 'Answer "Yes" to the Honors College question in the Common App\'s Academics section and complete the additional required writing supplement.', source: 'howToApply' },
        ],
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Letters sent beginning in December', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'Letters sent beginning in February', binding: false },
        ],
        note: 'The Honors College has a priority deadline of November 1 and a regular deadline of February 1, with its decisions released separately.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'SAT or ACT scores are optional at UMBC. If you graduated from a U.S. high school within the past three years, you may have the testing agency send your results. UMBC publishes its 25th to 75th percentile SAT range, 1240 to 1420, so you can judge whether your scores would help your application.',
        source: 'howToApply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission but considered if submitted, for fall 2027 applicants, with scores due by February 1.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [14086, 34183] },
            { item: 'Housing (on campus)', amounts: [9248, 9428] },
            { item: 'Food', amounts: [6490, 6490] },
            { item: 'Books', amounts: [1600, 1600] },
            { item: 'Transportation', amounts: [1900, 1900] },
            { item: 'Other', amounts: [7546, 7546] },
            { item: 'Direct Loan fee', amounts: [142, 142] },
            { item: 'PLUS Loan fee', amounts: [1264, 1264] },
        ],
        totals: [42276, 62553],
        note: 'For a full-time undergraduate living on campus, per year. UMBC\'s page gives an in-state total of $42,456, which is $180 above the sum of its own items; its in-state housing figure ($9,248) differs from the out-of-state one ($9,428). Tuition and fees are estimates based on proposed rates.',
        source: 'cost',
        netPrice: {
            average: 25203,
            byIncome: [
                { income: '$0 to $30,000', amount: 21797 },
                { income: '$30,001 to $48,000', amount: 21831 },
                { income: '$48,001 to $75,000', amount: 23232 },
                { income: '$75,001 to $110,000', amount: 26911 },
                { income: 'Over $110,000', amount: 29330 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UMBC\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
